import { useEffect, useState } from "react";
import { useToast } from "./ui/use-toast";
import { Button } from "./ui/button";
import { Sparkles, AlertTriangle, ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils";
import PlanSelectionModal from "./modals/PlanSelectionModal";
import { usePlansWithFeatures } from "@/hooks/use-plans-data";
import { useTrialStatus } from "@/hooks/useTrialStatus";
import { Badge } from "./ui/badge";

export function TrialBanner() {
  const { toast } = useToast();
  const [showPlansModal, setShowPlansModal] = useState(false);
  const { data: plans, error } = usePlansWithFeatures();
  const trialStatus = useTrialStatus();

  useEffect(() => {
    if (error) {
      console.error("Error fetching plans:", error);
      toast({
        title: "Error",
        description: "No se pudieron cargar los planes",
        variant: "destructive",
      });
    }
  }, [error, toast]);

  const handleUpdatePlan = () => {
    setShowPlansModal(true);
  };

  if (!trialStatus.isTrial || !trialStatus.endsAt) return null;

  const daysLeft = Math.ceil(
    (new Date(trialStatus.endsAt).getTime() - new Date().getTime()) /
    (1000 * 60 * 60 * 24)
  );

  return (
    <div className="w-full">
      <div className={cn(
        "p-3 rounded-xl border transition-all duration-300 animate-fade-in",
        trialStatus.isExpired
          ? "bg-gradient-to-br from-red-50 to-rose-50 border-red-100 text-red-950 shadow-sm"
          : "bg-gradient-to-br from-amber-50 to-orange-50 border-orange-100 text-orange-950 shadow-sm"
      )}>
        <div className="flex items-center gap-2 mb-1">
          {trialStatus.isExpired ? (
            <AlertTriangle className="h-3.5 w-3.5 text-red-600 animate-pulse" />
          ) : (
            <Sparkles className="h-3.5 w-3.5 text-orange-600 animate-pulse" />
          )}
          <span className="text-[11px] font-bold">
            {trialStatus.isExpired ? "Prueba Expirada" : "Prueba Gratis"}
          </span>
          <Badge
            variant="secondary"
            className={cn(
              "text-[8px] px-1.5 py-0 h-3.5 ml-auto border-none",
              trialStatus.isExpired
                ? "bg-red-100 text-red-800"
                : "bg-orange-100 text-orange-800"
            )}
          >
            {trialStatus.isExpired ? "Expirado" : "Activo"}
          </Badge>
        </div>

        <p className="text-[10px] text-gray-500 mb-2 leading-relaxed">
          {trialStatus.isExpired
            ? "Tu periodo de prueba ha terminado. Por favor, selecciona un plan para continuar."
            : `Tu acceso gratuito terminará en ${daysLeft} días.`}
        </p>

        <Button
          size="sm"
          className={cn(
            "w-full font-bold text-[10px] h-7 rounded-lg transition-all shadow-sm flex items-center justify-center gap-1",
            trialStatus.isExpired
              ? "bg-red-600 hover:bg-red-700 text-white"
              : "bg-orange-600 hover:bg-orange-700 text-white"
          )}
          onClick={handleUpdatePlan}
        >
          {trialStatus.isExpired ? "Suscribirse Ahora" : "Ver Planes"}
          <ArrowRight className="h-3 w-3" />
        </Button>
      </div>

      <PlanSelectionModal
        isOpen={showPlansModal}
        onClose={() => setShowPlansModal(false)}
        plans={plans || []}
      />
    </div>
  );
}
